import {DestroyRef, inject, Injectable} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {MatDialog, MatDialogRef} from "@angular/material/dialog"
import {RenameDialogComponent} from "@app/common/components/dialogs/rename-dialog/rename-dialog.component"
import {CM_LOGGER_TEXTURES} from "@app/common/services/logging"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {RefreshService} from "@app/common/services/refresh/refresh.service"
import {TextureSetRevisionDataFragment} from "@app/textures/service/textures-api.generated"
import {TexturesApiService} from "app/textures/service/textures-api.service"
import {AdjustSizeDialogComponent, AdjustSizeDialogResult} from "@app/textures/texture-editor/adjust-size-dialog/adjust-size-dialog.component"
import {OperatorStack} from "@app/textures/texture-editor/operator-stack/operator-stack"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {TextureEditProcessing} from "@app/textures/texture-editor/texture-edit-processing"
import {getSourceMapDataObjectIds, TextureEditorCallback} from "@app/textures/texture-editor/texture-editor-callback"
import {TextureEditorDataService} from "@app/textures/texture-editor/texture-editor-data.service"
import {getRevisionTitle} from "@app/textures/utils/texture-set-revision-utils"
import {getSimpleJobState, SimpleJobState} from "app/textures/utils/simple-job-state"
import {deepCopy, deepEqual} from "@cm/utils"
import {IsDefined, IsUnique} from "@cm/utils/filter"
import {DialogComponent} from "@common/components/dialogs/dialog/dialog.component"
import {AuthService} from "@common/services/auth/auth.service"
import {JobState, TextureSetRevisionMapAssignmentInput} from "@generated"
import {filter, first, firstValueFrom} from "rxjs"

@Injectable()
export class TextureRevisionActionsService {
    private readonly dialog = inject(MatDialog)
    private readonly notification = inject(NotificationsService)
    private readonly textureApi = inject(TexturesApiService)
    private readonly refresh = inject(RefreshService)
    private readonly authService = inject(AuthService)
    private readonly textureEditProcessing = inject(TextureEditProcessing)
    private readonly dataService = inject(TextureEditorDataService)
    private readonly logger = inject(CM_LOGGER_TEXTURES)
    private readonly destroyRef = inject(DestroyRef)

    async adjustSize(): Promise<void> {
        const revision = this.dataService.$revision()
        if (!revision) {
            throw Error("No texture set revision data set.")
        }
        const resultDataObject = this.dataService.$textureTypeData()?.resultDataObject
        if (!resultDataObject) {
            throw Error("No texture type specific data set.")
        }
        if (!resultDataObject.width || !resultDataObject.height) {
            throw Error("Texture set revision has no width or height resolution set.")
        }
        const dialogRef: MatDialogRef<AdjustSizeDialogComponent, AdjustSizeDialogResult | undefined> = this.dialog.open(AdjustSizeDialogComponent, {
            width: "350px",
            data: {
                width: revision.textureSetRevision.width,
                height: revision.textureSetRevision.height,
                resolutionAspectRatio: resultDataObject.width / resultDataObject.height,
                displacement: revision.textureSetRevision.displacement,
                showDisplacementSetting: true,
                allowUndefinedDisplacement: true,
            },
        })
        const result = await firstValueFrom(dialogRef.afterClosed())
        if (!result) {
            return
        }
        await this.notification.withUserFeedback(
            () =>
                this.textureApi.updateTextureSetRevision({
                    id: revision.textureSetRevision.id,
                    width: result.width,
                    height: result.height,
                    displacement: result.displacement,
                }),
            {
                success: () => {
                    this.dataService.updateRevision((current) => ({
                        ...current,
                        textureSetRevision: {...current.textureSetRevision, width: result.width, height: result.height, displacement: result.displacement},
                    }))
                    return "Physical size adjusted. Note that the adjustment will not get automatically propagated to the materials which use the modified textures"
                },
                error: "Error adjusting physical size.",
            },
        )
    }

    renameRevision(): void {
        const revision = this.dataService.$revision()
        if (!revision) {
            throw Error("No texture set revision data set.")
        }
        const currentRevision = revision.textureSetRevision
        const dialogRef = this.dialog.open(RenameDialogComponent, {
            disableClose: false,
            width: "400px",
            data: {
                currentName: currentRevision.name,
                allowUndefined: true,
            },
        })
        dialogRef
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(async (newName) => {
                if (typeof newName === "string") {
                    if (newName === "") {
                        newName = null
                    }
                    void this.notification.withUserFeedback(
                        () => this.textureApi.updateTextureSetRevision({id: currentRevision.id, name: newName}),
                        {
                            success: () => {
                                this.dataService.updateRevision((current) => ({
                                    ...current,
                                    textureSetRevision: {...current.textureSetRevision, name: newName},
                                    sortedTextureSetRevisionDescriptors: current.sortedTextureSetRevisionDescriptors.map((descriptor) =>
                                        descriptor.id === currentRevision.id ? {...descriptor, name: newName} : descriptor,
                                    ),
                                }))
                                this.refresh.item({id: revision.textureSet.id, __typename: "TextureSet"})
                                return "Revision renamed."
                            },
                            error: "Error renaming texture revision.",
                        },
                    )
                }
            })
    }

    async copyCurrentRevision(callback: TextureEditorCallback, copyOperators: boolean): Promise<void> {
        const revision = this.dataService.$revision()
        if (!revision) {
            throw Error("No texture set revision data set.")
        }
        if (!this.dataService.$textureTypeData()) {
            throw Error("No texture type specific data set.")
        }
        callback.setBusy(true)
        try {
            const operatorStack = callback.$operatorStack()
            if (!operatorStack) {
                throw Error("No operator stack set.")
            }
            operatorStack.muteUpdates = true // prevent operator stack updates while copying
            try {
                const name = revision.textureSetRevision.name ? revision.textureSetRevision.name + " (copy)" : undefined
                const newTextureSetRevisionId = await this.textureApi
                    .createNewTextureSetRevision({
                        textureSetId: revision.textureSet.id,
                        name,
                        width: revision.textureSetRevision.width,
                        height: revision.textureSetRevision.height,
                        mapAssignments: revision.textureSetRevision.mapAssignments.map((assignment) => ({
                            textureType: assignment.textureType,
                            dataObjectId: assignment.dataObject.id,
                        })),
                    })
                    .then((result) => result.createTextureSetRevision.id)
                await this.saveAndStartProcessing(callback, {targetRevisionId: newTextureSetRevisionId, copyOperators})
                callback.setTextureSetRevision(newTextureSetRevisionId, {warnIfEdited: false, forceReload: true})
            } finally {
                operatorStack.muteUpdates = false // re-enable operator stack updates
            }
        } finally {
            callback.setBusy(false)
        }
    }

    async importOperatorsFromRevision(callback: TextureEditorCallback): Promise<void> {
        const operatorStack = callback.$operatorStack()
        if (!operatorStack) {
            throw Error("No operator stack set.")
        }

        // Step 1: Ask for texture set legacy ID
        const idDialogRef = this.dialog.open(RenameDialogComponent, {
            disableClose: false,
            width: "400px",
            data: {
                currentName: "",
                caption: "Enter texture set legacy ID",
                confirmLabel: "Load",
            },
        })
        const textureSetLegacyId = await firstValueFrom(idDialogRef.afterClosed())
        if (!textureSetLegacyId || typeof textureSetLegacyId !== "string") {
            return
        }
        const parsedLegacyId = parseInt(textureSetLegacyId.trim(), 10)
        if (isNaN(parsedLegacyId)) {
            this.notification.showError("Invalid legacy ID. Please enter a numeric ID.")
            return
        }

        // Step 2: Fetch texture set and its revisions (show busy indicator during network requests)
        let selectedEditsJson: TextureEditNodes.TextureEdits
        callback.setBusy(true)
        try {
            const textureSetData = await this.textureApi.queryTextureSetByLegacyId(parsedLegacyId)
            const revisionDescriptors = textureSetData.textureSetRevisions

            // Step 3: Sort revisions like the editor tabs and fetch each revision's editsJson
            const sortedDescriptors = [...revisionDescriptors]
                .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
                .map((revision, index) => ({...revision, version: index + 1}))
                .reverse()

            const revisionsWithEdits: {version: number; name: string | null | undefined; editsJson: TextureEditNodes.TextureEdits}[] = []
            for (const descriptor of sortedDescriptors) {
                const revisionData = await this.textureApi.queryTextureSetRevision(descriptor.id)
                if (
                    revisionData.editsJson &&
                    revisionData.editsJson.type === "texture-edits" &&
                    revisionData.editsJson.schema >= TextureEditNodes.MIN_SUPPORTED_SCHEMA_VERSION
                ) {
                    revisionsWithEdits.push({
                        version: descriptor.version,
                        name: descriptor.name,
                        editsJson: revisionData.editsJson as TextureEditNodes.TextureEdits,
                    })
                }
            }

            if (revisionsWithEdits.length === 0) {
                this.notification.showError("No revisions with valid editing steps found in this texture set.")
                return
            }

            // Step 4: Pick a revision
            if (revisionsWithEdits.length === 1) {
                selectedEditsJson = revisionsWithEdits[0].editsJson
            } else {
                const otherActions = revisionsWithEdits.map((rev, index) => ({
                    state: String(index),
                    label: getRevisionTitle(rev, rev.version),
                }))
                const pickDialogRef = this.dialog.open(DialogComponent, {
                    disableClose: false,
                    width: "400px",
                    data: {
                        title: "Select revision",
                        message: "Select a revision to copy operators from:",
                        confirmLabel: "",
                        cancelLabel: "Cancel",
                        otherActions,
                    },
                })
                const picked = await firstValueFrom(pickDialogRef.afterClosed())
                if (picked === false || picked == null) {
                    return
                }
                selectedEditsJson = revisionsWithEdits[Number(picked)].editsJson
            }
        } catch (error) {
            this.notification.showError("Error importing operators: " + error)
            return
        } finally {
            callback.setBusy(false)
        }

        // Step 5: apply the imported edits directly - the source image is unaffected (same revision, same
        // texture type), so no need to touch anything but the operator stack's edits
        if (!this.dataService.$revision() || !this.dataService.$textureTypeData()) {
            this.notification.showError("No texture data available.")
            return
        }
        this.dataService.updateRevision((current) => ({...current, persistedTextureEdits: selectedEditsJson}))
        // Mark operators as edited once the result image has been rendered
        const subscription = operatorStack.resultImageChanged.pipe(filter(IsDefined), first()).subscribe(() => {
            for (const operator of operatorStack.operators) {
                if (!operator.flags.has("is-internal")) {
                    operator.markEdited()
                }
            }
        })
        subscription.add(() => subscription.unsubscribe())
        this.notification.showInfo("Operators imported successfully.")
        await operatorStack.setTextureEdits(selectedEditsJson)
    }

    async deleteCurrentRevision(callback: TextureEditorCallback): Promise<void> {
        const revision = this.dataService.$revision()
        if (!revision) {
            throw Error("No texture set revision data set.")
        }
        callback.setBusy(true)
        try {
            const textureSetRevisionToDelete = revision.textureSetRevision
            const referencedByMaterials = textureSetRevisionToDelete.materialRevisionReferences
                .map(
                    (reference) =>
                        `${reference.materialRevision.material.name} (${reference.materialRevision.material.legacyId}) v${reference.materialRevision.number}`,
                )
                .filter(IsUnique)
            if (referencedByMaterials.length > 0) {
                this.notification.showError(
                    "The selected revision is referenced by a material node and can not be deleted. Please delete the referencing material node first.\n\nThe referencing material revisions are:\n" +
                        referencedByMaterials.join(", "),
                    10000,
                )

                return
            }
            const isLastRevision = revision.textureSet.textureSetRevisions.length === 1
            await this.notification
                .confirmationDialog({
                    title: "Delete current revision",
                    message: isLastRevision
                        ? `This is the last revision. Deleting it will also <strong>permanently remove all map data</strong>. This action cannot be undone. Are you sure?`
                        : `Are you sure you want to delete the current revision?`,
                    confirm: "Delete",
                    cancel: "Abort",
                    isDestructive: true,
                })
                .then(async (confirmed) => {
                    if (confirmed) {
                        const jobState = this.dataService.$editsProcessingJob()?.state
                        const isProcessingEdits = !!jobState && getSimpleJobState(jobState) !== SimpleJobState.Complete
                        if (isProcessingEdits) {
                            // in case we're still processing edits, cancel those
                            await this.textureEditProcessing.cancelBakingJob(this.dataService.$editsProcessingJob())
                        }
                        await this.notification.withUserFeedback(() => this.textureApi.deleteTextureSetRevision(textureSetRevisionToDelete.id), {
                            success: () => {
                                const currentRevisionIndex = revision.textureSet.textureSetRevisions.findIndex((r) => r.id === textureSetRevisionToDelete.id)
                                if (currentRevisionIndex < 0) {
                                    throw Error("INTERNAL ERROR: No texture set revision found.")
                                }
                                let nextRevisionIndex: number | undefined
                                if (currentRevisionIndex < revision.textureSet.textureSetRevisions.length - 1) {
                                    nextRevisionIndex = currentRevisionIndex + 1
                                } else if (currentRevisionIndex > 0) {
                                    nextRevisionIndex = currentRevisionIndex - 1
                                }
                                if (nextRevisionIndex != null) {
                                    callback.setTextureSetRevision(revision.textureSet.textureSetRevisions[nextRevisionIndex].id, {
                                        warnIfEdited: false,
                                    })
                                } else {
                                    callback.closeDialogIfClean()
                                }
                                return "Revision deleted."
                            },
                            error: "Error deleting texture edit.",
                        })
                    }
                })
        } finally {
            callback.setBusy(false)
        }
    }

    async save(callback: TextureEditorCallback): Promise<void> {
        if (!callback.$operatorStack()) {
            throw Error("No operator stack set.")
        }
        if (!this.dataService.$revision()) {
            throw Error("No texture set revision data set.")
        }
        callback.setBusy(true)
        try {
            await this.notification.withUserFeedback(
                async () => {
                    await this.saveAndStartProcessing(callback)
                },
                {
                    success: () => "Saved successfully.",
                    error: (msg) => "Saving failed: " + msg,
                },
            )
        } finally {
            callback.setBusy(false)
        }
    }

    discardChanges(callback: TextureEditorCallback): void {
        const revision = this.dataService.$revision()
        if (!revision) {
            throw Error("No texture set revision data set.")
        }
        const operatorStack = callback.$operatorStack()
        if (!operatorStack) {
            throw Error("No operator stack set.")
        }
        this.notification
            .confirmationDialog({
                title: "Discard changes",
                message: `Are you sure you want to discard the current changes ?`,
                confirm: "Discard",
                cancel: "Abort",
                isDestructive: true,
            })
            .then(async (confirmed) => {
                if (confirmed) {
                    await operatorStack.setTextureEdits(revision.persistedTextureEdits ?? null)
                }
            })
    }

    async maybePersistCacheBackfill(callback: TextureEditorCallback): Promise<void> {
        function roundFloatsToComparisonPrecision(value: unknown): unknown {
            const comparisonPrecision = 9 // significant digits; above GPU/curve-fit float noise, below any real difference
            if (typeof value === "number") {
                return Number.isFinite(value) ? Number(value.toPrecision(comparisonPrecision)) : value
            }
            if (Array.isArray(value)) {
                return value.map(roundFloatsToComparisonPrecision)
            }
            if (value && typeof value === "object") {
                return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, roundFloatsToComparisonPrecision(entry)]))
            }
            return value
        }

        // operator save() can re-derive fields from live state (e.g. OperatorTiling's curve-fit normalizedCurvePosition) or
        // GPU-reduction stats (recolor/highpass sourceStatsCache) that are never bit-exact across a load->save round-trip
        function textureEditsAreEquivalent(a: TextureEditNodes.TextureEdits, b: TextureEditNodes.TextureEdits | null): boolean {
            return deepEqual(roundFloatsToComparisonPrecision(a), roundFloatsToComparisonPrecision(b))
        }

        const operatorStack = callback.$operatorStack()
        const revision = this.dataService.$revision()
        if (!operatorStack || !revision) {
            return
        }
        if (!callback.$isDataValid()) {
            return
        }
        try {
            const textureSetRevision = revision.textureSetRevision
            if (operatorStack.$isEdited()) {
                this.logger.warn("isEdited is set even so we just loaded initially. This should not happen")
                return
            }
            const freshTextureEdits = await this.collectTextureEdits(operatorStack, textureSetRevision, undefined)
            const currentTextureEdits: TextureEditNodes.TextureEdits = JSON.parse(JSON.stringify(freshTextureEdits)) // drop explicit-undefined keys a real JSON payload never has
            const persistedTextureEdits = revision.persistedTextureEdits ?? null
            if (textureEditsAreEquivalent(currentTextureEdits, persistedTextureEdits)) {
                return
            }
            this.logger.log("Updating texture edits json due to divergence.")
            await this.textureApi.updateTextureSetRevision({
                id: textureSetRevision.id,
                editsJson: currentTextureEdits,
                updatedAt: textureSetRevision.updatedAt ?? textureSetRevision.createdAt,
                updatedBy: textureSetRevision.updatedBy?.id ?? textureSetRevision.createdBy?.id,
            })
            this.dataService.updateRevision((current) => ({
                ...current,
                textureSetRevision: {...current.textureSetRevision, editsJson: currentTextureEdits},
                persistedTextureEdits: currentTextureEdits,
            }))
        } catch (e) {
            this.logger.warn("Failed to persist texture edit cache backfill", e)
        }
    }

    // copy: persist to that freshly created revision instead of the loaded one, leaving the loaded revision's state and the live
    // operator stack untouched (the caller navigates to the copy afterward). Without copyOperators the stack is not involved at all.
    private async saveAndStartProcessing(callback: TextureEditorCallback, copy?: {targetRevisionId: string; copyOperators: boolean}): Promise<void> {
        const revision = this.dataService.$revision()
        if (!revision) {
            throw Error("No texture set revision data set.")
        }
        const operatorStack = callback.$operatorStack()
        if (!operatorStack) {
            throw Error("No operator stack set.")
        }
        const textureSetRevision = copy ? {...revision.textureSetRevision, id: copy.targetRevisionId} : revision.textureSetRevision
        const previousEditsProcessingJob = copy ? undefined : this.dataService.$editsProcessingJob()
        const operatorStackToSave = copy?.copyOperators === false ? null : operatorStack
        const pxPerCm = this.dataService.$pxPerCm()?.width
        if (!pxPerCm) {
            throw Error("No pixel per cm set.")
        }
        if (operatorStackToSave) {
            operatorStackToSave.setBusy(true)
            try {
                await operatorStackToSave.refreshFullStackInHalMode()
            } finally {
                operatorStackToSave.setBusy(false)
            }
        }
        const processingJobId = await this.textureEditProcessing.createBakingJob(operatorStackToSave, textureSetRevision, pxPerCm, previousEditsProcessingJob)
        const textureEdits = await this.collectTextureEdits(operatorStackToSave, textureSetRevision, processingJobId)
        let mapAssignments: TextureSetRevisionMapAssignmentInput[] | undefined = undefined
        if (processingJobId) {
            // if we spawn a new processing job then we attach the original source maps until it is done and remove the old baked maps if they are unused
            const sourceMapDataObjectIds = getSourceMapDataObjectIds(textureSetRevision)
            mapAssignments = Array.from(sourceMapDataObjectIds.entries()).map(([textureType, dataObjectId]) => ({
                textureType,
                dataObjectId,
            }))
        }
        await this.textureApi.updateTextureSetRevision({
            id: textureSetRevision.id,
            editsProcessingJobId: processingJobId,
            editsJson: textureEdits,
            mapAssignments,
            autoRescaleSize: true,
        })
        if (processingJobId) {
            this.refresh.item({id: revision.textureSet.id, __typename: "TextureSet"})
        }
        if (copy) {
            return
        }
        operatorStack.resetChangedFlag()
        const updatedAt = new Date().toISOString()
        const updatedBy = await this.authService.user
        const detachedTextureEdits = deepCopy(textureEdits) // the saved nodes are the operators' live nodes - later edits must not leak into the persisted state
        this.dataService.updateRevision((current) => ({
            ...current,
            textureSetRevision: {
                ...current.textureSetRevision,
                updatedAt,
                updatedBy,
                editsJson: detachedTextureEdits
            },
            persistedTextureEdits: detachedTextureEdits,
        }))
        if (processingJobId) {
            this.dataService.$editsProcessingJob.set({id: processingJobId, state: JobState.Init, __typename: "Job"})
        }
        await callback.checkForProcessingEdits()
    }

    private async collectTextureEdits(
        operatorStack: OperatorStack | null,
        textureSetRevision: TextureSetRevisionDataFragment,
        processingJobId: string | undefined,
    ): Promise<TextureEditNodes.TextureEdits> {
        const operators = operatorStack?.operators ?? []
        const operatorNodes = (await Promise.all(operators.map((operator) => operator.save(processingJobId)))).filter(IsDefined)
        return this.makeTextureEditsFromOperatorNodes(textureSetRevision, operatorNodes)
    }

    private makeTextureEditsFromOperatorNodes(
        textureSetRevision: TextureSetRevisionDataFragment,
        operatorNodes: TextureEditNodes.Operator[],
    ): TextureEditNodes.TextureEdits {
        const textureRevisionId = getSourceMapDataObjectIds(textureSetRevision)
        const dataObjectByTextureType: TextureEditNodes.TextureSet["dataObjectByTextureType"] = {}
        textureRevisionId.forEach((id, textureType) => {
            dataObjectByTextureType[textureType] = {
                type: "data-object-reference",
                dataObjectId: id,
            }
        })
        return {
            type: "texture-edits",
            schema: TextureEditNodes.CURRENT_SCHEMA,
            maps: {
                type: "texture-set",
                dataObjectByTextureType,
            },
            operators: operatorNodes,
        }
    }
}
